import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import ErrorBanner from '../../components/shared/ErrorBanner'
import axiosInstance from '../../services/axios/axiosConfig'
import { endpoints } from '../../services/axios/endpoints'
import { errorMessage } from '../../services/axios/errorMessage'
import {
    VOLUME_MAX_DAYS_BACK,
    VOLUME_PRESETS,
    volumeDateProblem,
    volumeDayRange,
    volumePresetRange,
    volumeTotal,
} from '../../helpers/reports'
import { isoAddDays, istIsoDate, nowUnix } from '../../utils/datetime'
import { channelMeta } from '../PipelineWizard/wizardShared'
import { SURFACE, TEXT, cardSx } from '../../theme/tokens'

const cellSx = { borderColor: SURFACE.border, color: TEXT.secondary }

/** One channel's row, expanding to its count per event type. */
const VolumeRow = ({ row }) => {
    const [open, setOpen] = useState(false)
    const meta = channelMeta(row.comm_type)
    const breakdown = row.by_event_type ?? []
    return (
        <>
            <TableRow data-testid="volume-row">
                <TableCell sx={{ ...cellSx, width: 48 }}>
                    {breakdown.length > 0 && (
                        <IconButton
                            size="small"
                            aria-expanded={open}
                            aria-label={`${
                                open ? 'Hide' : 'Show'
                            } event types for ${meta.label}`}
                            onClick={() => setOpen(!open)}
                            sx={{ color: TEXT.muted }}
                        >
                            {open ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                            )}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ ...cellSx, color: TEXT.primary }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '& svg': { color: meta.color },
                        }}
                    >
                        {meta.icon}
                        {meta.label}
                    </Box>
                </TableCell>
                <TableCell sx={{ ...cellSx, color: TEXT.primary }}>
                    {row.count}
                </TableCell>
            </TableRow>
            {open &&
                breakdown.map((e) => (
                    <TableRow key={e.event_type} data-testid="event-type-row">
                        <TableCell sx={cellSx} />
                        <TableCell sx={{ ...cellSx, pl: 6 }}>
                            {e.event_type}
                        </TableCell>
                        <TableCell sx={cellSx}>{e.count}</TableCell>
                    </TableRow>
                ))}
        </>
    )
}

VolumeRow.propTypes = {
    row: PropTypes.shape({
        comm_type: PropTypes.string,
        count: PropTypes.number,
        by_event_type: PropTypes.arrayOf(
            PropTypes.shape({
                event_type: PropTypes.string,
                count: PropTypes.number,
            })
        ),
    }).isRequired,
}

/**
 * Comms Sent: how many comms went out per channel on one India-time day,
 * each channel expanding to its event types, with the total (GET
 * /comms/v2/comm_volume_report). Yesterday loads on open; any other day is
 * picked as one date. One day at a time: a week or a month read every comm
 * log of every channel in the range and timed out, so those choices went
 * (26 Oct 2026) and the server refuses a longer range. The server's reason
 * is shown when it refuses a day.
 */
const CommsSentCard = () => {
    const [preset, setPreset] = useState('yesterday')
    const [date, setDate] = useState(
        () => volumePresetRange('yesterday', nowUnix()).date
    )
    const [rows, setRows] = useState([])
    // The day the rows are for (the date input may already say otherwise).
    const [shown, setShown] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const requestRef = useRef(0)

    /** Load the counts for one day; an answer to an older request is dropped. */
    const load = async ({ start, end, date: day }) => {
        const request = ++requestRef.current
        setLoading(true)
        setError('')
        try {
            const resp = await axiosInstance.get(endpoints.commVolumeReport, {
                params: { start_time: start, end_time: end },
                silentToast: true,
                silentErrorToast: true,
                background: true,
            })
            if (request !== requestRef.current) return
            setRows(
                Array.isArray(resp?.data?.response) ? resp.data.response : []
            )
            setShown(day)
        } catch (err) {
            if (request !== requestRef.current) return
            setRows([])
            setShown(null)
            setError(errorMessage(err?.response))
        } finally {
            if (request === requestRef.current) setLoading(false)
        }
    }

    useEffect(() => {
        // Yesterday, once, on open; afterwards only on a choice.
        load(volumePresetRange('yesterday', nowUnix()))
    }, [])

    const chooseYesterday = () => {
        const next = volumePresetRange('yesterday', nowUnix())
        setPreset('yesterday')
        setDate(next.date)
        load(next)
    }

    const changeDate = (e) => {
        setPreset('date')
        setDate(e.target.value)
    }

    const searchDate = () => {
        const problem = volumeDateProblem(date, nowUnix())
        if (problem) {
            setError(problem)
            return
        }
        load(volumeDayRange(date))
    }

    const today = istIsoDate(nowUnix())
    const earliest = isoAddDays(today, -VOLUME_MAX_DAYS_BACK)

    return (
        <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
            <Typography
                variant="overline"
                component="h2"
                sx={{ color: TEXT.muted, display: 'block', mb: 0.5 }}
            >
                Comms sent
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT.muted, mb: 1.5 }}>
                One day at a time: yesterday, or a date you pick.
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 2,
                }}
            >
                {VOLUME_PRESETS.map((p) => (
                    <Button
                        key={p.key}
                        variant={preset === p.key ? 'contained' : 'outlined'}
                        aria-pressed={preset === p.key}
                        onClick={chooseYesterday}
                    >
                        {p.label}
                    </Button>
                ))}
                <TextField
                    id="comms-sent-date"
                    label="Date"
                    type="date"
                    value={date}
                    onChange={changeDate}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: earliest, max: today }}
                    sx={{ width: 170 }}
                />
                <Button
                    variant={preset === 'date' ? 'contained' : 'outlined'}
                    startIcon={<SearchIcon />}
                    onClick={searchDate}
                >
                    Search
                </Button>
            </Box>

            {error && (
                <Box sx={{ mb: 2 }}>
                    <ErrorBanner message={error} />
                </Box>
            )}

            {loading ? (
                <Box
                    role="status"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                        Loading comms sent...
                    </Typography>
                </Box>
            ) : (
                shown && (
                    <>
                        {rows.length === 0 ? (
                            <Typography
                                variant="body2"
                                sx={{ color: TEXT.secondary }}
                            >
                                No comms were sent on {shown}.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 360 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={cellSx} />
                                            <TableCell sx={cellSx}>
                                                <Typography variant="overline">
                                                    Communication Type
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Typography variant="overline">
                                                    Comms Sent
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => (
                                            <VolumeRow
                                                key={row.comm_type}
                                                row={row}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Typography
                            variant="subtitle2"
                            data-testid="volume-total"
                            sx={{ color: TEXT.primary, mt: 1.5 }}
                        >
                            Total: {volumeTotal(rows)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: TEXT.muted }}
                        >
                            {shown}, India time.
                        </Typography>
                    </>
                )
            )}
        </Paper>
    )
}

export default CommsSentCard
